import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';

import { BrowserApiError } from '../data/browser-api';
import { reportWorldNode, type WorldNodeReportReason } from '../data/world-node-report-api';

const reasons: readonly { value: WorldNodeReportReason; label: string }[] = [
  { value: 'private_property', label: 'Private property' },
  { value: 'inaccessible', label: 'Cannot be reached on foot' },
  { value: 'unsafe', label: 'Unsafe location' },
  { value: 'missing', label: 'Place is missing or closed' },
  { value: 'duplicate', label: 'Duplicate stop' },
  { value: 'other', label: 'Another problem' },
];

function failureMessage(error: Error): string {
  if (error instanceof BrowserApiError) {
    if (error.code === 'rate_limited') return 'Too many reports. Please try again later.';
    if (error.code === 'journey_interaction_locked') return 'Stop safely before sending a report.';
    if (error.code === 'world_node_not_found') return 'This stop is no longer available.';
    if (
      ['authentication_required', 'session_expired', 'csrf_missing', 'csrf_invalid'].includes(
        error.code,
      )
    ) {
      return 'Sign in again before sending your report.';
    }
  }
  return 'Your report could not be sent. Please try again when connected.';
}

export function StopReport({
  nodeId,
  online,
  safetyLocked,
}: {
  nodeId: string;
  online: boolean;
  safetyLocked: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<WorldNodeReportReason | ''>('');
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const select = useRef<HTMLSelectElement>(null);
  const success = useRef<HTMLParagraphElement>(null);
  const report = useMutation({
    mutationFn: (value: WorldNodeReportReason) => reportWorldNode(nodeId, value),
    retry: false,
  });
  const composing = open && !safetyLocked && !report.isSuccess;

  useEffect(() => {
    if (composing) select.current?.focus();
  }, [composing]);
  useEffect(() => {
    if (report.isSuccess) success.current?.focus();
  }, [report.isSuccess]);

  function cancel() {
    setOpen(false);
    setReason('');
    report.reset();
    trigger.current?.focus();
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!reason || !online || safetyLocked || report.isPending) return;
    report.mutate(reason);
  }

  return (
    <div className="stop-report">
      {report.isSuccess ? (
        <p className="stop-report__thanks" ref={success} role="status" tabIndex={-1}>
          Thanks — your report has been received.
        </p>
      ) : (
        <button
          aria-controls={`${id}-form`}
          aria-expanded={composing}
          className="stop-report__trigger"
          disabled={safetyLocked || report.isPending}
          onClick={() => (composing ? cancel() : setOpen(true))}
          ref={trigger}
          type="button"
        >
          Report this stop
        </button>
      )}
      {composing && (
        <form aria-label="Report this stop" id={`${id}-form`} onSubmit={submit}>
          <label htmlFor={`${id}-reason`}>What is wrong with this stop?</label>
          <select
            disabled={report.isPending}
            id={`${id}-reason`}
            onChange={(event) => setReason(event.target.value as WorldNodeReportReason | '')}
            ref={select}
            required
            value={reason}
          >
            <option value="" disabled>
              Choose a reason
            </option>
            {reasons.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {!online && <p role="status">Reconnect to send your report.</p>}
          {report.error && (
            <p className="inline-error" role="alert">
              {failureMessage(report.error)}
            </p>
          )}
          <div className="stop-report__actions">
            <button disabled={report.isPending} onClick={cancel} type="button">
              Cancel
            </button>
            <button disabled={!reason || !online || report.isPending} type="submit">
              {report.isPending ? 'Sending…' : 'Submit report'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
